/**
 * AgentPanel — the texting agent's card on a lead's page (texting-agent plan
 * §7.2). Shows the OPEN conversation's status/phase/spend with pause / resume /
 * take-over / close controls (close = C-9, inline confirm + optional reason),
 * an upcoming-call chip when a booked callback rides the payload (C-10,
 * rendered defensively — absent field = no chip), and — collapsed behind a
 * compact toggle so the card stays dense — the step-5 screen detail (C-11):
 * per-question verdict dots with captured quotes plus the session history
 * including pivots, all from GET /agent/conversations/:id (its first consumer).
 *
 * When the lead's primary study is agent-enabled and no conversation exists,
 * offers "Hand to agent" (§6.5 manual attach) with an opener-family picker
 * (C-3): "Auto by lead age" (default) omits opener_family so the backend
 * age-picks the D-21 band; an explicit choice posts it. Renders NOTHING when
 * the feature is dark or the contact has no agent surface at all.
 *
 * Reports the open conversation's state up via onThreadState so the thread can
 * show its draft-pending strip (C-12) and the composer its "manual send pauses
 * the agent" warning (C-13) without any second fetch or polling loop.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Bot, PauseCircle, PlayCircle, Hand, Loader2, XCircle, PhoneCall,
  ChevronDown, ChevronRight, AlertTriangle, AlertCircle, ClipboardCheck,
} from 'lucide-react';
import api from '../../services/api';
import { subscribeAgentEvents } from '../../lib/realtimeTransport';
import { prettySlug, relTime } from '../lib';
import { AgentEscalationLike, AgentEscalationPayload } from './escalationDisplay';
import { btnGhost } from './agent/shared';
import type { DraftRow } from './agent/shared';
import type { AgentInFlight } from './agent/AgentDraftDock';
import DeterminationComposer, { type DeterminationGroup } from './agent/DeterminationComposer';
// The "Guide agent" popover — the owner's seven steers behind one button.
import GuideAgent from './agent/GuideAgent';

/** What the lead page needs to know about the agent's presence on this thread. */
export interface AgentThreadState {
  /** Open conversation status: active | paused | awaiting_review | handed_off. */
  status: string;
  /** A copilot turn sits in `drafted` awaiting approval (C-12's cue). */
  draftPending: boolean;
  /** The open conversation id — the escalation banner keys off it. */
  conversationId: string;
  /** Open/claimed escalations on this thread — the lead-page banner renders
   *  these with context and links back to where the concern was. */
  escalations: AgentEscalationLike[];
  /** Pending turns awaiting a human (drafted + guardrail-suppressed), oldest
   *  first — the lead-page draft dock renders these with approve/edit/reject
   *  so releasing a reply no longer means a trip to the console. */
  drafts: DraftRow[];
  /** ISO instant the agent began writing, or null. TTL-expired SERVER-side
   *  (crmAgentConsole.DRAFTING_TTL_MS) so a worker that died mid-generation
   *  cannot leave a permanent "writing…" on the page. */
  draftingSince: string | null;
  /** Approved replies that have not gone out yet, with why they are waiting. */
  inFlight: AgentInFlight[];
  /** Re-read this card's data — the dock calls it after approve/reject so the
   *  released draft leaves without waiting for the next realtime ping. */
  refresh: () => void;
}

interface Props {
  contactId: string;
  /** The lead's primary/considering study id, when known — enables "Hand to agent". */
  studyId?: string | null;
  /** Reports the OPEN conversation's state (null = none) after every fetch so
   *  CommsThread/Composer render their coexistence cues from THIS card's data. */
  onThreadState?: (state: AgentThreadState | null) => void;
  /** Escalation banner deep-link: a screening flag was clicked — open the
   *  screen detail and highlight this question. `focusNonce` re-triggers the
   *  jump even when the same question is clicked twice. */
  focusScreenKey?: string | null;
  focusNonce?: number;
  /** Phase 4 (D-4): drop the card box when the rail's Agent tab provides the
   *  one chrome. Classic mounts omit it — false renders exactly today's card. */
  frameless?: boolean;
  /** Phase 4 (D-4): rail-provided element. When set AND a conversation exists,
   *  the compact status line (state · phase · Take over) portals into it so
   *  agent visibility and the takeover control never depend on which rail tab
   *  is open. The line's Take over rides the SAME act()/busy gate as the
   *  card's own buttons — one implementation, one in-flight guard. */
  statusLineSlot?: HTMLElement | null;
  /** Phase 4 (D-4): reports whether this lead has ANY agent surface (open
   *  conversation, or the Hand-to-agent offer) — the rail shows/hides its
   *  Agent tab from this. Fired like onThreadState: after every fetch and on
   *  the contact-change reset. */
  onPresence?: (p: { loaded: boolean; hasConversation: boolean; canOffer: boolean }) => void;
}

interface ConversationRow {
  id: string;
  status: string;
  pause_reason: string | null;
  mode: string;
  phase: string | null;
  turns: number;
  messages_sent: number;
  cost_usd: number;
  open_flags: number;
  /** ISO of the next booked callback (C-10) — server-populated; may be absent. */
  upcoming_callback_at?: string | null;
}

interface DetailSession {
  id: string;
  study_id: string | null;
  study_name?: string | null;
  phase: string | null;
  started_at: string | null;
  ended_at: string | null;
  end_reason: string | null;
}

interface DetailTurn {
  id: string;
  status: string;
  actions?: unknown;
  created_at: string;
  /** The copy awaiting approval (`drafted`/`suppressed`) — the dock's payload. */
  draft_body?: string | null;
  /** The edited text an approver sent, when it differed from the draft. */
  final_body?: string | null;
  validator_results?: unknown;
  /** Set once the reply actually went out; its absence on an `approved` turn is
   *  what makes that turn "in flight". */
  outbound_communication_id?: string | null;
}

interface DetailEscalation {
  id: string;
  kind: string;
  status?: string;
  created_at?: string;
  payload?: AgentEscalationPayload | null;
}

/** §6.6b: one per-question row of the server-computed eligibility profile. */
interface DetailScreenItem {
  question_key: string;
  question: string | null;
  answered: boolean;
  declined: boolean;
  value: unknown;
  /** The lead's answer in WORDS when the stored value is an option key
   *  ("option_4" → "Heart attack"); absent when the value reads as itself. */
  value_label?: string | null;
  verdict: 'pass' | 'fail' | 'unknown' | null;
  quote: string | null;
  /** Where the answer came from: 'questionnaire' | 'landing' | 'prescreen'
   *  (prefilled from a prior submission) or 'conversation'. */
  source?: string | null;
}

interface DetailScreen {
  session_id: string;
  total: number;
  answered: number;
  complete: boolean;
  items?: DetailScreenItem[];
}

interface ConversationDetail {
  conversation: ({
    upcoming_callback_at?: string | null;
    /** The "agent is writing" pulse, TTL-expired server-side. */
    drafting_since?: string | null;
  } & Record<string, unknown>) | null;
  sessions?: DetailSession[];
  turns?: DetailTurn[];
  escalations?: DetailEscalation[];
  /** §6.6b server-computed screen state; null/absent on older servers. */
  screen?: DetailScreen | null;
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Agent is handling this thread',
  paused: 'Agent paused',
  awaiting_review: 'Screen complete — awaiting review',
  handed_off: 'Handed to the team',
};

/** Compact wording for the rail's pinned status line (D-4); the card keeps STATUS_LABEL. */
const STATUS_SHORT: Record<string, string> = {
  active: 'Agent active',
  paused: 'Agent paused',
  awaiting_review: 'Awaiting review',
  handed_off: 'Handed to team',
};

/** C-3: the engage opener families ('auto' = omit → backend age-picks). */
const OPENER_OPTIONS: Array<{ value: 'auto' | 'fresh' | 'reengage_recent' | 'reengage_aged'; label: string }> = [
  { value: 'auto', label: 'Auto by lead age' },
  { value: 'fresh', label: 'Fresh lead greeting' },
  { value: 'reengage_recent', label: 'Re-engage — recent lead' },
  { value: 'reengage_aged', label: 'Re-engage — aged lead' },
];

/** Staff close reasons (subset of the service vocabulary; '' = server default). */
const CLOSE_REASON_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Team is taking it from here' },
  { value: 'completed_no_fit', label: 'Screened — not a fit' },
  { value: 'wrong_person', label: 'Wrong person' },
  { value: 'dormant', label: 'Lead went quiet' },
];

/**
 * Machine refusal reason → what a recruiter needs to read. Keys are the
 * `error` values the agent routes return: crmAgent `canEngage`'s gate
 * vocabulary (plus the compliance gate's `opted_out`/`consent_revoked` it
 * passes through), `openConversation`/`engageContact`'s own refusals, and the
 * lifecycle routes' `status_conflict`. Every one of these used to reach the
 * console only — the owner clicked "Hand to agent" on a lead a teammate had
 * just texted (§6.1 human-recency) and saw nothing happen at all.
 *
 * Unmapped reasons are NOT swallowed: noticeFrom falls through to the
 * server's `detail`, then the raw reason, so a new backend reason degrades to
 * something readable instead of silence.
 */
const REFUSAL_MESSAGE: Record<string, string> = {
  // §6.1 — the one that bit us. The window is a tenant setting the server
  // resolves and does not send back, so the copy stays honest about "recently".
  human_recent: 'A teammate texted, emailed, or called this lead recently — the agent stays out until that goes quiet.',
  agent_disabled: 'The texting agent is switched off for this network.',
  study_not_enabled: 'The agent is not enabled for this study yet.',
  opted_out: 'This lead opted out of texts, so the agent cannot message them.',
  consent_revoked: 'This lead withdrew texting consent, so the agent cannot message them.',
  sms_blocked: 'Texting is blocked for this lead right now.',
  no_phone: 'This lead has no mobile number for the agent to text.',
  conversation_exists: 'The agent already has an open conversation with this lead.',
  concurrency_cap: 'The network is at its limit of open agent conversations — try again as some close.',
  contact_archived: 'This lead is archived.',
  contact_merged: 'This lead was merged into another record — hand off that one instead.',
  no_contact: 'This lead no longer exists — reload the page.',
  contact_wrong_network: 'This lead belongs to a different network.',
  study_wrong_network: 'That study belongs to a different network.',
  missing_ids: 'Missing the lead or study — reload the page and try again.',
  invalid_opener_family: 'That opening message is not available — pick another.',
  opener_enqueue_failed: 'The agent could not queue its opening text — try again.',
  gate_check_failed: 'Could not check whether the agent may take this lead — try again.',
  status_conflict: 'This conversation just changed somewhere else — reload to see where it stands.',
  error: 'Something went wrong — try again.',
};

/** A refusal (the server said no) reads as a warning; anything else is a fault. */
interface Notice { tone: 'warn' | 'error'; text: string }

/**
 * Build the visible notice from an axios error. The routes answer
 * `{ error: <reason>, detail }` and pick the STATUS by class — 409 for the
 * gate refusals, 4xx/502 for bad input and faults — so the status alone tells
 * us whether to read as policy or breakage.
 */
const noticeFrom = (err: any, fallback: string): Notice => {
  const data = err?.response?.data;
  const reason = typeof data?.error === 'string' ? data.error : null;
  const detail = typeof data?.detail === 'string' ? data.detail : null;
  const text = (reason && REFUSAL_MESSAGE[reason])
    || detail
    || (reason ? prettySlug(reason) : null)
    || err?.message
    || fallback;
  return { tone: err?.response?.status === 409 ? 'warn' : 'error', text };
};

// The same verdict palette as the prescreen surfaces (PivotPanel/ScreeningTab).
const VERDICT_DOT: Record<string, string> = {
  pass: 'bg-green-500',
  fail: 'bg-red-500',
  unknown: 'bg-amber-400 dark:bg-amber-500',
  // Owner rule 2026-07-30: on the questionnaire backbone most questions carry
  // NO eligibility rule ("what name do you go by?"). Those are recorded, not
  // judged — a distinct neutral dot, never the amber "needs review" one, or the
  // panel would contradict the summary note's "0 for review".
  none: 'bg-gray-300 dark:bg-gray-600',
};

const VERDICT_SR: Record<string, string> = {
  pass: 'passed:',
  fail: 'failed:',
  unknown: 'needs review:',
  none: 'recorded:',
};

/** 'ps.sim.v1.age' / 'pf.tobacco_use' → 'Age' / 'Tobacco Use'. */
const questionLabel = (key: string): string => prettySlug(key.split('.').pop() || key);

const answerLabel = (value: unknown): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined) return 'Declined to answer';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

interface ScreenRow {
  key: string;
  label: string;
  /** Full question text for the hover title (server rows only). */
  title?: string | null;
  verdict: 'pass' | 'fail' | 'unknown' | 'none';
  valueLabel: string;
  quote: string | null;
  pivot: boolean;
  /** §6.6b: 'questionnaire' | 'landing' | 'prescreen' when prefilled. */
  source?: string | null;
}

/** §6.6b source → the tiny provenance chip label (null = no chip). */
const SOURCE_CHIP: Record<string, string> = {
  questionnaire: 'questionnaire',
  landing: 'web form',
  prescreen: 'earlier screen',
  earlier_chat: 'earlier text chat',
  derived: 'calculated',
};

export default function AgentPanel({
  contactId, studyId = null, onThreadState, focusScreenKey = null, focusNonce = 0,
  frameless = false, statusLineSlot = null, onPresence,
}: Props) {
  const [convo, setConvo] = useState<ConversationRow | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [canOffer, setCanOffer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // C-3: opener-family choice for "Hand to agent" ('auto' posts nothing).
  const [openerFamily, setOpenerFamily] = useState<'auto' | 'fresh' | 'reengage_recent' | 'reengage_aged'>('auto');
  // C-9: inline close confirm (open + optional reason).
  const [closing, setClosing] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  // C-11: the screen/session detail stays collapsed by default (density bar).
  const [showDetail, setShowDetail] = useState(false);
  // Owner ask 2026-08-04: mid-screen flag determinations collapse behind a
  // toggle (awaiting_review shows the composer without asking).
  const [showDetermination, setShowDetermination] = useState(false);
  // Banner deep-link: the screen row currently flashed after a jump-to.
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  // Why an action was refused/failed — the gates are chatty and the recruiter
  // has to see them, not the console.
  const [notice, setNotice] = useState<Notice | null>(null);

  // Stable ref so a parent's inline callback never re-triggers the fetch effect.
  const onThreadStateRef = useRef(onThreadState);
  onThreadStateRef.current = onThreadState;
  const onPresenceRef = useRef(onPresence);
  onPresenceRef.current = onPresence;

  const fetchState = useCallback(async () => {
    try {
      // The board list already enriches + scopes by network; filter client-side
      // to this contact (open statuses only — closed history stays off the card).
      const res = await api.get<{ conversations: Array<ConversationRow & { contact_id: string }> }>(
        '/api/crm/agent/conversations', { params: { status: 'open', limit: 200 } },
      );
      const mine = (res.data?.conversations || []).find((c) => c.contact_id === contactId) || null;
      setConvo(mine);
      if (mine) {
        setCanOffer(false);
        // C-11: the conversation detail — turns (validator-checked), sessions
        // incl. pivots, and silent prescreen flags. Fail-soft: the card still
        // renders its v1 controls without it.
        const det = await api.get<ConversationDetail>(`/api/crm/agent/conversations/${mine.id}`).catch(() => null);
        setDetail(det?.data?.conversation ? det.data : null);
      } else {
        setDetail(null);
        if (studyId) {
          // Review H-6: the member-readable availability probe — the config
          // read is admin-gated, so probing it hid "Hand to agent" from every
          // basic recruiter (rollout stage (a)'s mechanism).
          const avail = await api.get(`/api/crm/agent/studies/${studyId}/available`).catch(() => null);
          setCanOffer(Boolean((avail as any)?.data?.available));
        } else {
          setCanOffer(false);
        }
      }
    } catch {
      /* dark / no access — render nothing */
    } finally {
      setLoaded(true);
    }
  }, [contactId, studyId]);

  useEffect(() => {
    const unsub = subscribeAgentEvents((p) => {
      if (!p.contactId || p.contactId === contactId) fetchState();
    });
    fetchState();
    return () => { unsub(); };
  }, [fetchState, contactId]);

  // Open/claimed escalations for THIS thread — lifted up so the conversation
  // card can show the escalation banner without a second fetch. The enriched
  // payload (verbatim + screening question + rule) rides along.
  const openEscalations = useMemo<AgentEscalationLike[]>(() => {
    if (!convo) return [];
    return (detail?.escalations || [])
      .filter((e) => e.status === 'open' || e.status === 'claimed')
      .map((e) => ({
        id: e.id,
        kind: e.kind,
        status: e.status || 'open',
        payload: e.payload || null,
        created_at: e.created_at,
        conversation_id: convo.id,
      }));
  }, [convo, detail]);

  // The pending drafts the lead-page dock renders: `drafted` is the ordinary
  // copilot wait, `suppressed` is the guardrail wall's salvage case (approving
  // one is the human taking responsibility — DraftCard says so in red). Oldest
  // first, matching the console queue's order.
  const pendingDrafts = useMemo<DraftRow[]>(() => {
    if (!convo) return [];
    return (detail?.turns || [])
      .filter((t) => t.status === 'drafted' || t.status === 'suppressed')
      .map((t) => ({
        id: t.id,
        conversation_id: convo.id,
        status: t.status,
        draft_body: t.draft_body || '',
        validator_results: t.validator_results,
        created_at: t.created_at,
      }));
  }, [convo, detail]);

  // Approved but not yet sent — a humanized delay, quiet hours, or a §6.7b
  // human-first window. Without this the thread reads as though the approval
  // did nothing.
  const inFlight = useMemo<AgentInFlight[]>(() => (detail?.turns || [])
    .filter((t) => (t.status === 'approved' || t.status === 'sending') && !t.outbound_communication_id)
    .map((t) => {
      const actions = (t.actions || {}) as Record<string, any>;
      return {
        id: t.id,
        body: t.final_body || t.draft_body || '',
        holdUntil: actions.human_first?.release_at || null,
        deferredUntil: actions.deferred_until || null,
      };
    }), [detail]);

  // C-12/C-13: report the thread state up whenever what we know changes.
  useEffect(() => {
    onThreadStateRef.current?.(convo ? {
      status: convo.status,
      draftPending: pendingDrafts.some((d) => d.status === 'drafted'),
      conversationId: convo.id,
      escalations: openEscalations,
      drafts: pendingDrafts,
      draftingSince: (detail?.conversation?.drafting_since as string | null) || null,
      inFlight,
      refresh: fetchState,
    } : null);
  }, [convo, detail, openEscalations, pendingDrafts, inFlight, fetchState]);

  // D-4: agent-surface presence for the rail's Agent tab. The contact-change
  // reset below flips loaded false, so navigation reports through here too.
  useEffect(() => {
    onPresenceRef.current?.({ loaded, hasConversation: !!convo, canOffer });
  }, [loaded, convo, canOffer]);

  // D-4: on unmount (the beta toggle flipping this card between its classic and
  // rail homes), clear what we reported — a surviving thread-state object would
  // hand the next mount's consumers a stale conversation id and a refresh
  // closure bound to a dead instance. Parent-unmount setState is a no-op.
  useEffect(() => () => {
    onThreadStateRef.current?.(null);
    onPresenceRef.current?.({ loaded: false, hasConversation: false, canOffer: false });
  }, []);

  // Reset when navigating between leads (the page is NOT remounted): transient
  // UI plus the fetched state itself, so the previous lead's conversation can
  // never paint this one's card — or feed the composer/thread cues — while the
  // refetch is in flight.
  useEffect(() => {
    setClosing(false);
    setCloseReason('');
    setShowDetail(false);
    setShowDetermination(false);
    setOpenerFamily('auto');
    setNotice(null);
    setConvo(null);
    setDetail(null);
    setCanOffer(false);
    setLoaded(false);
    setHighlightKey(null);
  }, [contactId]);

  // Escalation banner deep-link (§ "take you to where the concern was"): open
  // the screen detail and flash the clicked question's row. Falls back to
  // scrolling the whole card into view when that question has no visible row
  // yet. focusNonce re-fires the jump when the same flag is clicked again.
  //
  // The thread's summary card sends the same jump, and summary notes written
  // before the key was stamped carry only the question TEXT — so a target that
  // matches no question_key is resolved against the screen's question wording
  // before we give up on it (owner ask 2026-08-05).
  useEffect(() => {
    if (!focusScreenKey) return undefined;
    setShowDetail(true);
    const items = detail?.screen?.items || [];
    const norm = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
    const target = items.some((it) => it.question_key === focusScreenKey)
      ? focusScreenKey
      : (items.find((it) => norm(it.question) === norm(focusScreenKey))?.question_key ?? focusScreenKey);
    const jump = window.setTimeout(() => {
      const el = document.getElementById(`agent-screen-${target}`)
        || document.getElementById('agent-panel-card');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightKey(target);
    }, 80);
    const clear = window.setTimeout(
      () => setHighlightKey((k) => (k === target ? null : k)),
      2800,
    );
    return () => { window.clearTimeout(jump); window.clearTimeout(clear); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusScreenKey, focusNonce]);

  const act = async (path: string, body?: Record<string, unknown>) => {
    setBusy(true);
    setNotice(null);
    try {
      await api.post(path, body ?? {});
      await fetchState();
    } catch (err) {
      console.error('[AgentPanel] action failed:', err);
      setNotice(noticeFrom(err, 'That action failed — try again.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmClose = async () => {
    if (!convo) return;
    const reason = closeReason.trim();
    await act(`/api/crm/agent/conversations/${convo.id}/close`, reason ? { reason } : {});
    setClosing(false);
    setCloseReason('');
  };

  // Owner ask 2026-08-04: the determination happens ON the lead page — the
  // recruiter reads the chart and decides right here, no trek back to the
  // console. awaiting_review = the eligibility determination (composer shown
  // outright); open mid-screen flags = an early-call determination behind a
  // toggle. Both keep a deep link to the exact review-queue spot.
  const determinationCtx = useMemo<{ group: DeterminationGroup; escalationId: string | null } | null>(() => {
    if (!convo || !['active', 'paused', 'awaiting_review'].includes(convo.status)) return null;
    if (convo.status === 'awaiting_review') {
      const handoff = [...openEscalations].reverse()
        .find((esc) => esc.kind === 'handoff_review' || esc.kind === 'dormant_handback') || null;
      return { group: 'eligibility', escalationId: handoff?.id || null };
    }
    const flag = [...openEscalations].reverse().find((esc) => esc.kind === 'prescreen_flag') || null;
    return flag ? { group: 'screening', escalationId: flag.id } : null;
  }, [convo, openEscalations]);

  // The current session's study (the composer excludes it from pivot targets).
  const currentStudyId = (detail?.sessions?.length
    ? detail.sessions[detail.sessions.length - 1].study_id
    : null) || studyId || null;

  // C-10: upcoming booked call — from the row or the detail payload, whichever
  // the server populated; an absent/unparseable value renders no chip.
  const upcoming = useMemo(() => {
    const iso = convo?.upcoming_callback_at ?? detail?.conversation?.upcoming_callback_at ?? null;
    if (!iso || typeof iso !== 'string') return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [convo, detail]);

  // §6.6b: the server-computed eligibility profile is the preferred truth —
  // it includes prefilled-from-submission answers (which never appear in turn
  // actions) and REAL per-question verdicts (the turn-derived fallback below
  // defaulted every unflagged answer to 'pass'). Pivot answers still come
  // from turn actions — they live outside the screen state.
  const serverScreenRows = useMemo<ScreenRow[]>(() => {
    const items = detail?.screen?.items;
    if (!Array.isArray(items)) return [];
    return items.filter((it) => it.answered).map((it) => ({
      key: it.question_key,
      // The lead reads the real question ("How much do you weigh in pounds?"),
      // not the form's auto-generated key ("og0wm8i6"). Fall back to the
      // prettified key only when the server sent no question text.
      label: it.question || questionLabel(it.question_key),
      title: it.question || null,
      // A declined ELIGIBILITY question is a real unknown for the reviewer; a
      // declined (or simply rule-less) informational question is neither
      // passing nor pending — it is just recorded.
      verdict: it.verdict || (it.declined ? 'unknown' : 'none'),
      // The lead's WORDS win over the stored option value ("option_4").
      valueLabel: it.declined ? 'Declined to answer' : (it.value_label || answerLabel(it.value)),
      quote: it.quote || null,
      pivot: false,
      source: it.source || null,
    }));
  }, [detail]);

  // C-11: per-question screen progress. Captures come from the turns'
  // record_answer / record_pivot_answer actions (latest capture per key wins —
  // the supersede chain); real fail/unknown verdicts ride the silent
  // prescreen_flag escalations (§6.2-5). Recruiter-visible only — the server
  // already gates this payload.
  const screenRows = useMemo<ScreenRow[]>(() => {
    if (!detail) return [];
    const byKey = new Map<string, ScreenRow>();
    for (const t of detail.turns || []) {
      const actions = Array.isArray(t.actions) ? t.actions : [];
      for (const a of actions as Array<Record<string, unknown>>) {
        if (!a || typeof a !== 'object') continue;
        const isAnswer = a.type === 'record_answer';
        const isPivot = a.type === 'record_pivot_answer';
        if (!isAnswer && !isPivot) continue;
        const key = String((isAnswer ? a.question_key : a.profile_key) || '');
        if (!key) continue;
        byKey.set(key, {
          key,
          label: questionLabel(key),
          verdict: a.value === null || a.value === undefined ? 'unknown' : 'pass',
          valueLabel: answerLabel(a.value),
          quote: typeof a.quote === 'string' && a.quote ? a.quote : null,
          pivot: isPivot,
        });
      }
    }
    for (const e of detail.escalations || []) {
      if (e.kind !== 'prescreen_flag') continue;
      const key = e.payload?.question_key;
      const verdict = e.payload?.verdict;
      if (!key || (verdict !== 'fail' && verdict !== 'unknown')) continue;
      const row = byKey.get(key);
      if (row) {
        byKey.set(key, { ...row, verdict });
      } else {
        // Derived criteria (age/BMI calc) can flag without a spoken capture.
        byKey.set(key, {
          key, label: questionLabel(key), verdict, valueLabel: 'Derived', quote: null, pivot: false,
        });
      }
    }
    return [...byKey.values()];
  }, [detail]);

  // Server rows win for the screening half; turn-derived rows fill in pivot
  // answers (and everything, on older servers that send no `screen`).
  const combinedRows = useMemo<ScreenRow[]>(() => {
    if (!serverScreenRows.length) return screenRows;
    return [...serverScreenRows, ...screenRows.filter((r) => r.pivot)];
  }, [serverScreenRows, screenRows]);

  const sessions = detail?.sessions || [];
  const hasDetail = combinedRows.length > 0 || sessions.length > 1 || (sessions.length === 1 && Boolean(sessions[0].ended_at));
  // The header counts ELIGIBILITY questions only — the same denominator the
  // screening summary note uses, so the two surfaces never disagree.
  const judgedRows = combinedRows.filter((r) => r.verdict !== 'none');
  const passCount = judgedRows.filter((r) => r.verdict === 'pass').length;

  if (!loaded || (!convo && !canOffer)) return null;

  return (
    <>
      {/* D-4: the rail's pinned status line — same component instance, same
          busy gate, portalled above the rail tabs so takeover never depends on
          which tab is open. Renders only when the rail provides a slot. */}
      {statusLineSlot && convo ? createPortal(
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 dark:border-gray-700 dark:bg-gray-800">
          <Bot className="h-3.5 w-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          <span className="min-w-0 truncate text-xs font-medium text-gray-700 dark:text-gray-200">
            {STATUS_SHORT[convo.status] || convo.status}
            {convo.pause_reason ? ` (${convo.pause_reason.replace(/_/g, ' ')})` : ''}
          </span>
          <span className="whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">· phase {convo.phase || '—'}</span>
          {busy && <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin text-gray-400" aria-hidden="true" />}
          {['active', 'paused', 'awaiting_review'].includes(convo.status) && (
            <button
              type="button"
              disabled={busy}
              onClick={() => act(`/api/crm/agent/conversations/${convo.id}/takeover`)}
              className="ml-auto inline-flex min-h-[44px] flex-shrink-0 items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 sm:min-h-0"
            >
              <Hand className="h-3 w-3" /> Take over
            </button>
          )}
        </div>,
        statusLineSlot
      ) : null}
    <div id="agent-panel-card" className={frameless ? 'scroll-mt-16' : 'scroll-mt-16 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800'}>
      <div className="flex items-center gap-2">
        <Bot className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Texting agent</h3>
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
      </div>
      {/* One notice slot for the whole card — both branches' actions land here. */}
      {notice && (
        <div
          role="status"
          aria-live="polite"
          className={`mt-2 flex items-start gap-1.5 rounded-lg border p-2.5 text-xs ${
            notice.tone === 'warn'
              ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          {notice.tone === 'warn'
            ? <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            : <AlertCircle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
          <span className="min-w-0">{notice.text}</span>
        </div>
      )}
      {convo ? (
        <div className="mt-2">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {STATUS_LABEL[convo.status] || convo.status}
            {convo.pause_reason ? ` (${convo.pause_reason.replace('_', ' ')})` : ''}
          </p>
          {/* Mode · phase · sent · spend · flags. Rendered as wrap-safe
              segments so a narrow (phone) card breaks BETWEEN facts instead of
              mid-token — "$0.— / 04 spend" was a real 375px wrap. */}
          <div className="mt-1 flex flex-wrap items-center gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
            {[
              convo.mode,
              `phase ${convo.phase || '—'}`,
              `${convo.messages_sent} sent`,
              `$${Number(convo.cost_usd || 0).toFixed(2)} spend`,
              ...(convo.open_flags > 0 ? [`${convo.open_flags} open flag${convo.open_flags === 1 ? '' : 's'}`] : []),
            ].map((seg, i) => (
              <span key={seg} className="whitespace-nowrap">
                {i > 0 && <span aria-hidden="true" className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>}
                {seg}
              </span>
            ))}
          </div>
          {upcoming && (
            <p className="mt-1.5">
              <span
                title="The agent booked this call and holds its nudges until after it"
                className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
              >
                <PhoneCall className="h-3 w-3" />
                Call booked · {upcoming.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {convo.status === 'active' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => act(`/api/crm/agent/conversations/${convo.id}/pause`)}
                className={btnGhost}
              >
                <PauseCircle className="h-4 w-4" /> Pause
              </button>
            )}
            {convo.status === 'paused' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => act(`/api/crm/agent/conversations/${convo.id}/resume`)}
                className={btnGhost}
              >
                <PlayCircle className="h-4 w-4" /> Resume
              </button>
            )}
            {['active', 'paused', 'awaiting_review'].includes(convo.status) && (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => act(`/api/crm/agent/conversations/${convo.id}/takeover`)}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
                >
                  <Hand className="h-4 w-4" /> Take over
                </button>
                {/* C-9: the manual close — previously the only exits were
                    takeover or waiting out dormancy. */}
                <button
                  type="button"
                  disabled={busy}
                  aria-expanded={closing}
                  onClick={() => setClosing((v) => !v)}
                  className={btnGhost}
                >
                  <XCircle className="h-4 w-4" /> Close
                </button>
              </>
            )}
          </div>
          {closing && (
            <div className="mt-2 rounded-lg border border-red-200 bg-red-50/60 p-2.5 dark:border-red-900/50 dark:bg-red-900/15">
              <p className="text-xs font-medium text-red-700 dark:text-red-300">
                Close this conversation? The agent stops texting this lead — hand it to the agent again later if needed.
              </p>
              <select
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                aria-label="Close reason (optional)"
                className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              >
                {CLOSE_REASON_OPTIONS.map((o) => (
                  <option key={o.value || 'default'} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="mt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setClosing(false); setCloseReason(''); }}
                  className="rounded-lg px-2.5 py-1 text-xs font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={confirmClose}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors duration-150 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                  Close conversation
                </button>
              </div>
            </div>
          )}
          {/* Owner ask 2026-08-04: one-click steering, above the determination
              composer on purpose — the light touch should be the one you reach
              for first. A steer never pauses or hands off the conversation. */}
          <GuideAgent
            key={`guide-${convo.id}`}
            conversationId={convo.id}
            onError={(msg) => setNotice({ tone: 'error', text: msg })}
            onChanged={fetchState}
          />
          {/* Owner ask 2026-08-04: the determination composer on the lead
              page. awaiting_review renders it open (it IS the next action);
              a mid-screen flag hides it behind a toggle so the card stays
              dense. The queue deep link lands on this exact conversation. */}
          {determinationCtx && (
            <div className="mt-3 rounded-lg border border-primary-200 bg-primary-50/40 p-2.5 dark:border-primary-800 dark:bg-primary-900/10">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-gray-100">
                  <ClipboardCheck className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                  {determinationCtx.group === 'eligibility'
                    ? 'Screening complete — your determination'
                    : 'Flagged answer — determination optional'}
                </span>
                <span className="flex items-center gap-2">
                  {determinationCtx.group === 'screening' && (
                    <button
                      type="button"
                      aria-expanded={showDetermination}
                      onClick={() => setShowDetermination((v) => !v)}
                      className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
                    >
                      {showDetermination ? 'Hide' : 'Decide now'}
                    </button>
                  )}
                  <Link
                    to={`/crm/agent?tab=queue&review=${convo.id}`}
                    className="text-xs font-medium text-gray-500 hover:underline dark:text-gray-400"
                  >
                    Open in review queue
                  </Link>
                </span>
              </div>
              {(determinationCtx.group === 'eligibility' || showDetermination) && (
                <DeterminationComposer
                  key={`${convo.id}-${determinationCtx.group}`}
                  conversationId={convo.id}
                  group={determinationCtx.group}
                  currentStudyId={currentStudyId}
                  escalationId={determinationCtx.escalationId}
                  onSubmitted={fetchState}
                  onError={(msg) => setNotice({ tone: 'error', text: msg })}
                />
              )}
            </div>
          )}
          {/* C-11: screen progress + session history, collapsed by default. */}
          {hasDetail && (
            <div className="mt-3 border-t border-gray-100 pt-2 dark:border-gray-700/60">
              <button
                type="button"
                aria-expanded={showDetail}
                onClick={() => setShowDetail((v) => !v)}
                className="inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 sm:min-h-0"
              >
                {showDetail ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                Screen details
                {judgedRows.length > 0 && (
                  <span className="text-gray-400 dark:text-gray-500">
                    · {passCount}/{judgedRows.length} passing
                  </span>
                )}
              </button>
              {showDetail && (
                <div className="mt-1 space-y-2">
                  {combinedRows.length > 0 && (
                    <ul>
                      {combinedRows.map((r) => (
                        <li
                          key={r.key}
                          id={`agent-screen-${r.key}`}
                          className={`flex items-start gap-2 rounded-lg py-1 transition-colors duration-500 ${
                            highlightKey === r.key
                              ? 'bg-primary-50 ring-2 ring-primary-300 dark:bg-primary-900/20 dark:ring-primary-700'
                              : ''
                          }`}
                        >
                          <span
                            className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${VERDICT_DOT[r.verdict]}`}
                            aria-hidden="true"
                          />
                          <span className="sr-only">{VERDICT_SR[r.verdict]}</span>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-700 dark:text-gray-200" title={r.title || undefined}>
                              {r.label}
                              {r.pivot && (
                                <span className="ml-1 rounded-full bg-purple-100 px-1.5 text-[10px] font-medium text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                                  pivot
                                </span>
                              )}
                              {r.source && SOURCE_CHIP[r.source] && (
                                <span
                                  title={r.source === 'derived' ? 'Calculated from their answers' : 'Pre-filled from what the lead already told us'}
                                  className="ml-1 rounded-full bg-sky-100 px-1.5 text-[10px] font-medium text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
                                >
                                  {SOURCE_CHIP[r.source]}
                                </span>
                              )}
                              <span className="text-gray-400 dark:text-gray-500"> · </span>
                              <span className="font-medium text-gray-700 dark:text-gray-200">{r.valueLabel}</span>
                            </p>
                            {r.quote && (
                              <p className="mt-0.5 truncate text-[11px] italic text-gray-400 dark:text-gray-500" title={r.quote}>
                                “{r.quote}”
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {sessions.length > 0 && (
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Sessions</p>
                      <ul className="mt-0.5">
                        {sessions.map((s, i) => (
                          <li key={s.id} className="flex items-baseline gap-1.5 py-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                            <span className="min-w-0 truncate">
                              {i > 0 && <span className="font-medium text-purple-600 dark:text-purple-400">pivot → </span>}
                              {s.study_name || 'Study'}
                            </span>
                            <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                              · {s.ended_at ? (s.end_reason ? s.end_reason.replace(/_/g, ' ') : 'ended') : (s.phase || 'active')}
                              {' · '}{relTime(s.started_at)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {convo.status === 'active' && (
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              Sending a manual text or answering a call pauses the agent automatically.
            </p>
          )}
        </div>
      ) : (
        <div className="mt-2">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This study is agent-enabled. Hand this lead to the agent to run the conversation.
          </p>
          {/* C-3: opener-family picker — 'auto' lets the backend age-pick the
              D-21 band; an explicit family posts opener_family. */}
          <label className="mt-2 block text-xs font-medium text-gray-600 dark:text-gray-300" htmlFor="agent-opener-family">
            Opening message
          </label>
          <select
            id="agent-opener-family"
            value={openerFamily}
            onChange={(e) => setOpenerFamily(e.target.value as typeof openerFamily)}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            {OPENER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={busy || !studyId}
            onClick={async () => {
              if (!studyId) return;
              setBusy(true);
              setNotice(null);
              try {
                await api.post(`/api/crm/contacts/${contactId}/agent/engage`, {
                  study_id: studyId,
                  ...(openerFamily !== 'auto' ? { opener_family: openerFamily } : {}),
                });
                await fetchState();
              } catch (err) {
                // The §6.1/§6.12 gates refuse for good reasons — say which one.
                console.error('[AgentPanel] engage failed:', err);
                setNotice(noticeFrom(err, 'Could not hand this lead to the agent — try again.'));
              } finally {
                setBusy(false);
              }
            }}
            className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
          >
            <Bot className="h-4 w-4" /> Hand to agent
          </button>
        </div>
      )}
    </div>
    </>
  );
}
